import { Injectable } from '@angular/core';
import { Profile, UserRoles } from '../models';
import { AppService, ProductSuite } from './app.service';

interface ProfileReadinessIssueDetails {
  code: string;
  label: string;
  description: string;
  action: 'profile' | 'support';
}

export interface ProfileReadinessOption {
  profile: Profile;
  agentProfileId: string;
  company: string;
  isReady: boolean;
  isDisabled: boolean;
  issues: string[];
}

export interface ProfileReadinessFlags {
  bypassProfileStatus?: boolean;
  bypassRealEstateRole?: boolean;
}

const productExclusions: Partial<Record<ProductSuite, string>> = {
  [ProductSuite.LISTING_CONCIERGE]: 'LC',
  [ProductSuite.DESIGN_CONCIERGE]: 'DC',
};

const issueCopy: Record<ProfileReadinessIssueDetails['code'], Omit<ProfileReadinessIssueDetails, 'code'>> = {
  profile_incomplete: {
    label: 'Profile incomplete',
    description: 'Complete the required profile information before placing an order.',
    action: 'profile',
  },
  missing_profile_photo: {
    label: 'Profile image missing',
    description: 'Add a profile image before placing an order.',
    action: 'profile',
  },
  missing_profile_image: {
    label: 'Headshot setup incomplete',
    description: 'Review and update your headshot in My Profile for profile, print, and video use.',
    action: 'profile',
  },
  missing_name: {
    label: 'Name missing',
    description: 'Add the preferred first and last name before placing an order.',
    action: 'profile',
  },
  missing_email: {
    label: 'Email missing',
    description: 'Add a preferred email before placing an order.',
    action: 'profile',
  },
  missing_primary_phone: {
    label: 'Primary phone missing',
    description: 'Add a primary phone number before placing an order.',
    action: 'profile',
  },
  missing_office_phone: {
    label: 'Office phone missing',
    description: 'Add an office phone number before placing an order.',
    action: 'profile',
  },
  missing_office_details: {
    label: 'Office details incomplete',
    description: 'The selected office is missing required name, address, or office code information.',
    action: 'support',
  },
  unmapped_marketing_area: {
    label: 'Marketing area unmapped',
    description: 'The selected office code needs to be mapped to a marketing area before placing an order.',
    action: 'support',
  },
  missing_brokers_name: {
    label: 'Broker\'s name missing',
    description: 'Add the required broker\'s name before placing an order.',
    action: 'profile',
  },
  missing_license: {
    label: 'License missing',
    description: 'Select or add the required license information before placing an order.',
    action: 'profile',
  },
};

@Injectable({ providedIn: 'root' })
export class ProfileReadinessService {
  buildProfileOptions(
    profiles: Profile[] = [],
    flags: ProfileReadinessFlags = {},
  ): ProfileReadinessOption[] {
    const appExclusion = productExclusions[AppService.productSuiteName];
    return profiles
      .filter((profile) => this.isRoleEligible(profile, flags))
      .map((profile) => this.buildProfileOption(profile, flags, appExclusion));
  }

  issueDetails(issues: string[] = []): ProfileReadinessIssueDetails[] {
    const issueDetails = issues.map((code) => {
      const copy = issueCopy[code] ?? {
        label: 'Review profile',
        description: 'Review this profile before placing an order.',
        action: 'support',
      };

      return { ...copy, code };
    });
    return issueDetails;
  }

  private buildProfileOption(
    profile: Profile,
    flags: ProfileReadinessFlags,
    appExclusion: string | undefined,
  ): ProfileReadinessOption {
    const isExcluded = this.isExcluded(profile, appExclusion);
    const issues = profile.status?.issues ?? [];
    const isComplete = profile.status?.isComplete === true && issues.length === 0;

    return {
      profile,
      agentProfileId: profile._id,
      company: profile.office?.company?.companyName,
      isReady: isComplete && !isExcluded,
      isDisabled: !flags.bypassProfileStatus && (isExcluded || !isComplete),
      issues,
    };
  }

  private isRoleEligible(profile: Profile, flags: ProfileReadinessFlags) {
    const bypassRoleCheck = flags.bypassRealEstateRole;
    const isAgent = profile.realEstateRole === UserRoles.AGENT;
    const isDesignConcierge = AppService.isDesignConciergeProduct;
    return bypassRoleCheck || isAgent || isDesignConcierge;
  }

  private isExcluded(profile: Profile, appExclusion: string | undefined) {
    if (!appExclusion) return false;
    return profile.status?.appExclusions?.includes(appExclusion) ?? false;
  }
}
